import { useState } from 'react'
import { describeDue, type LetterTask } from '@core/letters'
import { describeRepeat } from '@core/taskRepeat'
import { Card, Section } from '@design/components'

/**
 * A group of to-dos, and one row.
 *
 * Two targets per row, and no more. The circle on the left finishes the task —
 * that is the one tap that has to be right, so it gets the left edge and the
 * thumb. Everything else about the task (its wording, its date, whose it is,
 * throwing it away) lives behind the row itself, in TodoSheet.
 *
 * There used to be a small × on the right for deleting. It sat a few
 * millimetres from the checkbox and did something irreversible: the two most
 * common actions on the row were "done" and "gone", side by side, at thumb
 * size. That is a bad row.
 */
export function TodoList({
  title,
  tasks,
  nameOf,
  onDone,
  onOpen,
}: {
  title: string
  tasks: LetterTask[]
  nameOf: (userId: string | null) => string | null
  onDone: (task: LetterTask) => Promise<void>
  onOpen: (task: LetterTask) => void
}) {
  if (tasks.length === 0) return null

  return (
    <Section title={title} count={tasks.length}>
      <div className="space-y-2">
        {tasks.map((task) => (
          <Row
            key={task.id}
            task={task}
            who={nameOf(task.assignedTo)}
            onDone={onDone}
            onOpen={onOpen}
          />
        ))}
      </div>
    </Section>
  )
}

function Row({
  task,
  who,
  onDone,
  onOpen,
}: {
  task: LetterTask
  who: string | null
  onDone: (task: LetterTask) => Promise<void>
  onOpen: (task: LetterTask) => void
}) {
  const [busy, setBusy] = useState(false)
  const due = describeDue(task.dueDate)
  const late = isLate(task.dueDate)

  return (
    <Card className="flex items-start gap-3">
      <button
        aria-label="Afvinken"
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          await onDone(task).catch(() => setBusy(false))
        }}
        className="shrink-0 mt-0.5 w-7 h-7 rounded-full border-2 border-line active:border-ink"
      />
      <button
        onClick={() => onOpen(task)}
        className="flex-1 min-w-0 text-left"
        aria-label={`${task.title} openen`}
      >
        <p className="font-medium">{task.title}</p>
        {task.assignmentNote && <p className="text-sm text-muted">{task.assignmentNote}</p>}
        <p className={`text-xs mt-0.5 ${late ? 'text-danger' : 'text-muted'}`}>
          {[who, due !== 'geen datum' ? due : null, describeRepeat(task.repeatRule)]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </button>
    </Card>
  )
}

/** Past its date and still open — the only thing on this row worth a colour. */
function isLate(dueDate: string | null, today = new Date()): boolean {
  if (!dueDate) return false
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return new Date(dueDate + 'T00:00:00').getTime() < start.getTime()
}
